/*
 * The reader-facing API calls.
 *
 * Every content read here goes through a published view on the server side -
 * kb.ArticlePublished for articles, kb.ReleaseNotePublished for release notes
 * (S42, 2026-10-12) - which is why none of them takes a "include unpublished"
 * flag: there is no way for this half of the client to ask for something a
 * reader should not see, so there is no way for a bug here to leak a draft.
 *
 * An article is addressed by a PAIR. kb.Article.Slug is unique only within a
 * product (UQ_Article_ProductId_Slug), so a request carrying a slug alone names
 * nothing in particular - two products may both have a "getting-started" - and
 * every article endpoint therefore takes the product slug and the article slug
 * together. A release note is a pair too, (product slug, version), for the same
 * kind of reason: UQ_ReleaseNote_ProductId_Version. Neither half is assembled
 * into a path here; see apiRelative.
 *
 * FOR A READER (2026-10-26, S46). Articles can be private - read only by their
 * audiences and by administrators - so each content read takes the PublicReader
 * the page has (usePublicReader, AuthProvider) and sends its token when there
 * is one. Still no flag that asks for more: the server decides from the token
 * what the reader may read, and a call without a reader is the anonymous one.
 */

import type {
  AttachmentDownloadLink,
  ClientConfig,
  SignInConfig,
  PublicArticle,
  PublicArticleListResponse,
  PublicArticleSummary,
  PublicBootstrap,
  PublicReleaseNote,
  PublicReleaseNoteListResponse,
  PublicSiteSettings,
} from '@accotool-kb/shared';
import {
  publicArticleAttachmentLinkPath,
  publicArticlePath,
  publicArticleRelatedPath,
  publicReleaseNotePath,
  publicReleaseNotesPath,
} from '@accotool-kb/shared';
import { request, type TokenProvider } from './client.js';
import type { PublicReader } from './publicReader.js';

/** The request option that sends the reader's token, if there is one. */
function asReader(reader: PublicReader | undefined): { readerToken?: TokenProvider } {
  return reader?.token ? { readerToken: reader.token } : {};
}


/**
 * The browse filters, as the query string carries them.
 *
 * Product and category ids are OR-ed within their own axis and AND-ed across
 * axes; the server does that, and BrowsePage says so on screen, because the
 * opposite reading is just as plausible. Products stay a LIST even though an
 * article has exactly one: this is the reader's selection, not the article's.
 */
export interface BrowseFilter {
  products: number[];
  categories: number[];
  q: string;
  page: number;
  pageSize: number;
  sort: 'recent' | 'title';
}

/**
 * The release-note list's filter, as its query string carries it: the products
 * (`?products=1,2`, the browse page's spelling, OR-ed), the search (`?q=`, as on
 * the browse page - S43, 2026-10-13; '' is no search and is not sent) and the
 * page. No sort: a search only narrows the list, which stays newest publish date
 * first (the owner's answer, 2026-10-13). pageSize is optional because the
 * server's default is the page size the reader-facing list uses
 * (PUBLIC_RELEASE_NOTE_PAGE_SIZE).
 */
export interface ReleaseNoteFilter {
  products: number[];
  q: string;
  page: number;
  pageSize?: number;
}

/*
 * The article paths are built by the shared package, which returns them WHOLE -
 * `/api/public/articles/...` - because that is the form an <img src>, a test
 * assertion and the server's own router all spell. `request` prepends `/api`
 * itself, so it takes the API-relative form, and handing it a whole path would
 * produce /api/api/public/articles.
 *
 * Trimming the prefix here rather than writing `/public/articles/${slug}` out by
 * hand is the whole point of the exercise. A hand-written path is a second
 * spelling of the address, and a second spelling is exactly what let call sites
 * sit on the old one-segment shape without the type checker noticing: a template
 * literal type-checks whatever you interpolate into it. There is one spelling of
 * an article's address; this adapts it to a helper that supplies the prefix.
 */
const API_PREFIX = '/api';

function apiRelative(wholePath: string): string {
  return wholePath.startsWith(API_PREFIX) ? wholePath.slice(API_PREFIX.length) : wholePath;
}

export const getClientConfig = (): Promise<ClientConfig> => request<ClientConfig>('/config');

/** The MSAL bootstrap. Fetched only when the admin area is entered - see AuthProvider. */
export const getSignInConfig = (): Promise<SignInConfig> =>
  request<SignInConfig>('/config/sign-in');

export const getBootstrap = (reader?: PublicReader): Promise<PublicBootstrap> =>
  request<PublicBootstrap>('/public/bootstrap', asReader(reader));

/**
 * The site's title, subtitle and icon on their own. The public shell gets the
 * same object inside the bootstrap; this is for the admin shell, which has no
 * bootstrap and still shows the brand.
 */
export const getSiteSettings = (): Promise<PublicSiteSettings> =>
  request<PublicSiteSettings>('/public/site');

export const listArticles = (
  filter: BrowseFilter,
  signal?: AbortSignal,
  reader?: PublicReader,
): Promise<PublicArticleListResponse> =>
  request<PublicArticleListResponse>('/public/articles', {
    query: {
      products: filter.products,
      categories: filter.categories,
      q: filter.q,
      page: filter.page,
      pageSize: filter.pageSize,
      sort: filter.sort,
    },
    ...(signal ? { signal } : {}),
    ...asReader(reader),
  });

export const getArticleBySlug = (
  productSlug: string,
  slug: string,
  reader?: PublicReader,
): Promise<PublicArticle> =>
  request<PublicArticle>(apiRelative(publicArticlePath(productSlug, slug)), asReader(reader));

/**
 * Resolves the permalink form. Answers 404 for anything a reader may not see.
 *
 * The one article address spelled out in this file rather than built in the
 * shared package, and deliberately so: it is not an address anything LINKS to.
 * It is the lookup that hands back the canonical pair, which ArticleByIdPage
 * then redirects to, so there is no call site for a builder to protect. That it
 * cannot be shadowed by a product called "by-id" is settled where products are
 * named - the schema reserves the word - not here.
 */
export const getArticleById = (articleId: string, reader?: PublicReader): Promise<PublicArticle> =>
  request<PublicArticle>(`/public/articles/by-id/${encodeURIComponent(articleId)}`, asReader(reader));

/** The promoted tiles, already filtered to live articles and in shortlist order. */
export const getPromotedArticles = (
  signal?: AbortSignal,
  reader?: PublicReader,
): Promise<{ items: PublicArticleSummary[] }> =>
  request<{ items: PublicArticleSummary[] }>('/public/promoted-articles', {
    ...(signal ? { signal } : {}),
    ...asReader(reader),
  });

export const getRelatedArticles = (
  productSlug: string,
  slug: string,
  reader?: PublicReader,
): Promise<{ items: PublicArticleSummary[] }> =>
  request<{ items: PublicArticleSummary[] }>(
    apiRelative(publicArticleRelatedPath(productSlug, slug)),
    asReader(reader),
  );

/**
 * A short-lived download link for one attachment, through the article it is
 * listed on or linked from (S47). The article is the permission: the server
 * signs a link only when this reader may read that article and the article uses
 * the file, and answers every other case with the same 404. Sent with the
 * reader's token, which is what makes a private article's files downloadable by
 * its audience - a plain link to the redirecting address cannot carry one.
 */
export const getAttachmentDownloadLink = (
  productSlug: string,
  slug: string,
  attachmentId: string,
  reader?: PublicReader,
): Promise<AttachmentDownloadLink> =>
  request<AttachmentDownloadLink>(
    apiRelative(publicArticleAttachmentLinkPath(productSlug, slug, attachmentId)),
    asReader(reader),
  );

/**
 * One page of published release notes, whole - every item carries its full
 * markdown and its own link resolutions - with the product filter's options
 * (products with live release notes, counted, narrowed by neither the products
 * nor the search) and the filter echoed back, the search trimmed.
 */
export const listReleaseNotes = (
  filter: ReleaseNoteFilter,
  signal?: AbortSignal,
  reader?: PublicReader,
): Promise<PublicReleaseNoteListResponse> =>
  request<PublicReleaseNoteListResponse>(apiRelative(publicReleaseNotesPath()), {
    query: {
      products: filter.products,
      q: filter.q,
      page: filter.page,
      ...(filter.pageSize !== undefined ? { pageSize: filter.pageSize } : {}),
    },
    ...(signal ? { signal } : {}),
    // The notes' links to private articles resolve for this reader (S46).
    ...asReader(reader),
  });

/** One published release note by its address. Answers 404 for anything a reader may not see. */
export const getReleaseNote = (
  productSlug: string,
  version: string,
  signal?: AbortSignal,
  reader?: PublicReader,
): Promise<PublicReleaseNote> =>
  request<PublicReleaseNote>(apiRelative(publicReleaseNotePath(productSlug, version)), {
    ...(signal ? { signal } : {}),
    ...asReader(reader),
  });

/*
 * The image endpoint has no wrapper here at all. It is `publicArticleImagePath`
 * in the shared package, imported straight by the two places that need an
 * <img src> - the reader's article page and the admin theme preview - because a
 * wrapper would only be a second name for one address, and the security note
 * that used to live on it belongs with the page doing the rendering: the
 * endpoint reads kb.ArticleImagePublished, so an image on an article a reader
 * may not see is a 404, indistinguishable from an image that does not exist.
 * Release-note images follow the same rule through publicReleaseNoteImagePath
 * and kb.ReleaseNoteImagePublished.
 */

/**
 * One image of a PRIVATE article, as an object URL (S46). An <img> cannot send
 * the reader's token, and the image endpoint serves a private article's images
 * only to whoever may read its text - so the article page fetches them with the
 * token, as the editor's preview fetches the admin images, and hands the element
 * a blob: URL. The caller revokes it. A public article's images need none of
 * this and are still a plain src.
 */
export async function fetchPrivateArticleImage(wholePath: string, reader: PublicReader): Promise<string> {
  const headers: Record<string, string> = {};
  if (reader.token) headers.Authorization = `Bearer ${await reader.token()}`;
  const response = await fetch(wholePath, { headers });
  if (!response.ok) throw new Error(`The image could not be loaded (${response.status}).`);
  return URL.createObjectURL(await response.blob());
}
